import { Play, Pause, X, Volume2, Radio, ExternalLink, SkipBack, SkipForward, Heart, ChevronUp } from "lucide-react";
import FullPlayer from "./FullPlayer";
import AddToPlaylistButton from "./AddToPlaylistButton";
import { usePlayer } from "../contexts/PlayerContext";
import { useFavorites } from "../contexts/FavoritesContext";
import { formatDuration } from "../lib/api";
import { Link } from "react-router-dom";

export default function AudioPlayer() {
    const { current, playing, progress, duration, volume, streamMode, toggle, seek, stop, setVolume, playNext, playPrev, hasNext, hasPrev, expanded, setExpanded } = usePlayer();
    const { isFavorite, toggle: toggleFav } = useFavorites();
    if (!current) return null;

    const inPlaylist = isFavorite(current.id);

    const pct = duration ? (progress / duration) * 100 : 0;
    const isFull = streamMode === "full";

    if (expanded) return <FullPlayer />;

    return (
        <div
            className="fixed bottom-0 inset-x-0 z-40 glass border-t border-[#262626]"
            data-testid="global-audio-player"
            style={{
                background: "linear-gradient(180deg, rgba(15,15,15,0.92) 0%, rgba(10,10,10,0.96) 100%)",
                backdropFilter: "blur(24px) saturate(160%)",
                WebkitBackdropFilter: "blur(24px) saturate(160%)",
                boxShadow: "0 -20px 60px -20px rgba(0,168,107,0.18), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
        >
            {/* Top accent line */}
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00d97e]/60 to-transparent" />

            {/* Mobile progress bar (seekable) */}
            <div className="md:hidden relative h-4 -mb-2 flex items-center px-4" data-testid="player-progress-mobile-wrap">
                <div className="absolute inset-x-4 h-1 rounded-full bg-[#262626] overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-[#ff6b35] via-[#ffc83d] to-[#00d97e]"
                        style={{ width: `${pct}%` }}
                    />
                </div>
                <input
                    type="range"
                    min={0}
                    max={duration || 0}
                    value={progress}
                    step={0.1}
                    onChange={(e) => seek(parseFloat(e.target.value))}
                    className="absolute inset-x-4 inset-y-0 w-[calc(100%-2rem)] opacity-0 cursor-pointer"
                    data-testid="player-progress-mobile"
                    aria-label="Position de lecture"
                />
            </div>
            <div className="md:hidden flex justify-between px-4 -mb-1">
                <span className="text-[10px] text-[#6E6E6E] font-mono tabular-nums">{formatDuration(progress)}</span>
                <span className="text-[10px] text-[#6E6E6E] font-mono tabular-nums">{formatDuration(duration)}</span>
            </div>

            <div className="max-w-7xl mx-auto px-3 md:px-12 h-[76px] md:h-[88px] flex items-center gap-2 md:gap-4">
                {/* Track info */}
                <button type="button" onClick={() => setExpanded(true)} className="flex items-center gap-3 min-w-0 flex-1 md:flex-none md:w-80 group text-left" aria-label="Agrandir le lecteur" data-testid="player-expand">
                    <div className="relative shrink-0">
                        {current.cover_url ? (
                            <img decoding="async" loading="lazy"
                                src={current.cover_url}
                                alt={current.title}
                                className={`w-12 h-12 md:w-14 md:h-14 rounded-lg object-cover shadow-lg transition-transform ${playing ? "ring-2 ring-[#00d97e]/60 ring-offset-2 ring-offset-[#0a0a0a]" : "ring-1 ring-[#262626]"}`}
                            />
                        ) : (
                            <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#1c1c1c] to-[#0F0F0F] border border-[#262626]" />
                        )}
                        {playing && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#00d97e] border-2 border-[#0a0a0a] flex items-center justify-center">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#0a0a0a] animate-pulse" />
                            </div>
                        )}
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold truncate group-hover:text-[#00d97e] transition-colors" data-testid="player-title">
                            {current.title}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-xs text-[#A8A8A8] truncate">{current.artist_name}</span>
                            <span
                                className={`shrink-0 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full ${
                                    isFull
                                        ? "bg-gradient-to-r from-[#00d97e]/20 to-[#00A86B]/20 text-[#00d97e] border border-[#00d97e]/40"
                                        : "bg-gradient-to-r from-[#D4AF37]/20 to-[#ffc83d]/20 text-[#ffc83d] border border-[#ffc83d]/40"
                                }`}
                                data-testid="player-mode-badge"
                            >
                                <Radio className="w-2.5 h-2.5" />
                                {isFull ? "Full" : "Preview"}
                            </span>
                        </div>
                    </div>
                    <ChevronUp className="w-4 h-4 text-[#6E6E6E] shrink-0 md:hidden" />
                </button>

                {/* Center: Prev + Play + Next + Progress */}
                <div className="hidden md:flex flex-1 items-center gap-3">
                    <button
                        onClick={playPrev}
                        disabled={!hasPrev && progress < 3}
                        className="w-9 h-9 rounded-full text-[#A8A8A8] hover:text-[#F4F4F4] hover:bg-[#1c1c1c] disabled:opacity-30 disabled:hover:bg-transparent flex items-center justify-center transition-all"
                        data-testid="player-prev"
                        aria-label="Précédent"
                        title="Précédent"
                    >
                        <SkipBack className="w-4 h-4" />
                    </button>
                    <button
                        onClick={toggle}
                        className="relative w-12 h-12 rounded-full bg-gradient-to-br from-[#00d97e] to-[#00A86B] hover:from-[#00f099] hover:to-[#00d97e] text-[#0a0a0a] flex items-center justify-center shadow-lg shadow-[#00d97e]/30 hover:scale-105 transition-transform"
                        data-testid="player-play-pause"
                        aria-label={playing ? "Pause" : "Play"}
                    >
                        {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                        {playing && <span className="absolute inset-0 rounded-full border-2 border-[#00d97e]/40 animate-ping" />}
                    </button>
                    <button
                        onClick={() => playNext(false)}
                        disabled={!hasNext}
                        className="w-9 h-9 rounded-full text-[#A8A8A8] hover:text-[#F4F4F4] hover:bg-[#1c1c1c] disabled:opacity-30 disabled:hover:bg-transparent flex items-center justify-center transition-all"
                        data-testid="player-next"
                        aria-label="Suivant"
                        title="Suivant"
                    >
                        <SkipForward className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => toggleFav(current)}
                        className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${inPlaylist ? "text-[#ff3d77]" : "text-[#A8A8A8] hover:text-[#ff3d77]"} hover:bg-[#1c1c1c]`}
                        data-testid="player-add-playlist"
                        aria-label={inPlaylist ? "Retirer des favoris" : "Ajouter aux favoris"}
                        title={inPlaylist ? "Retirer des favoris" : "Ajouter aux favoris"}
                    >
                        <Heart className={`w-4 h-4 ${inPlaylist ? "fill-[#ff3d77]" : ""}`} />
                    </button>
                    <AddToPlaylistButton track={current} testid="player-add-to-playlist" />
                    <span className="text-[11px] text-[#A8A8A8] font-mono tabular-nums w-10">{formatDuration(progress)}</span>
                    <div className="relative flex-1 group/seek h-8 flex items-center cursor-pointer">
                        <div className="absolute inset-x-0 h-1.5 rounded-full bg-[#262626] overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-[#ff6b35] via-[#ffc83d] to-[#00d97e] transition-all"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <div
                            className="absolute w-3 h-3 rounded-full bg-[#F4F4F4] shadow-md opacity-0 group-hover/seek:opacity-100 transition-opacity"
                            style={{ left: `calc(${pct}% - 6px)` }}
                        />
                        <input
                            type="range"
                            min={0}
                            max={duration || 0}
                            value={progress}
                            step={0.1}
                            onChange={(e) => seek(parseFloat(e.target.value))}
                            className="absolute inset-0 w-full opacity-0 cursor-pointer"
                            data-testid="player-progress"
                            aria-label="Position de lecture"
                        />
                    </div>
                    <span className="text-[11px] text-[#A8A8A8] font-mono tabular-nums w-10">{formatDuration(duration)}</span>
                </div>

                {/* Mobile controls: prev + play + next */}
                <div className="md:hidden flex items-center gap-0.5 shrink-0">
                    <button
                        onClick={playPrev}
                        disabled={!hasPrev && progress < 3}
                        className="w-9 h-9 rounded-full text-[#A8A8A8] disabled:opacity-30 flex items-center justify-center"
                        data-testid="player-prev-mobile"
                        aria-label="Précédent"
                    >
                        <SkipBack className="w-4 h-4" />
                    </button>
                    <button
                        onClick={toggle}
                        className="relative w-11 h-11 rounded-full bg-gradient-to-br from-[#00d97e] to-[#00A86B] text-[#0a0a0a] flex items-center justify-center shadow-lg shadow-[#00d97e]/30"
                        data-testid="player-play-pause-mobile"
                        aria-label={playing ? "Pause" : "Play"}
                    >
                        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                    </button>
                    <button
                        onClick={() => playNext(false)}
                        disabled={!hasNext}
                        className="w-9 h-9 rounded-full text-[#A8A8A8] disabled:opacity-30 flex items-center justify-center"
                        data-testid="player-next-mobile"
                        aria-label="Suivant"
                    >
                        <SkipForward className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => toggleFav(current)}
                        className={`w-9 h-9 rounded-full flex items-center justify-center ${inPlaylist ? "text-[#ff3d77]" : "text-[#A8A8A8]"}`}
                        data-testid="player-add-playlist-mobile"
                        aria-label="Favoris"
                    >
                        <Heart className={`w-4 h-4 ${inPlaylist ? "fill-[#ff3d77]" : ""}`} />
                    </button>
                    <AddToPlaylistButton track={current} testid="player-add-to-playlist-mobile" />
                </div>

                {/* Right: volume + detail link + close */}
                <div className="hidden lg:flex items-center gap-2 w-36 group/vol">
                    <Volume2 className="w-4 h-4 text-[#A8A8A8] group-hover/vol:text-[#F4F4F4] transition-colors" />
                    <div className="relative flex-1 h-6 flex items-center">
                        <div className="absolute inset-x-0 h-1 rounded-full bg-[#262626] overflow-hidden">
                            <div className="h-full bg-[#00d97e]" style={{ width: `${volume * 100}%` }} />
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.01}
                            value={volume}
                            onChange={(e) => setVolume(parseFloat(e.target.value))}
                            className="absolute inset-0 w-full opacity-0 cursor-pointer"
                            data-testid="player-volume"
                            aria-label="Volume"
                        />
                    </div>
                </div>

                <Link
                    to={`/track/${current.slug || current.id}`}
                    className="hidden md:inline-flex p-2 text-[#A8A8A8] hover:text-[#ffc83d] transition-colors"
                    data-testid="player-open-detail"
                    aria-label="Ouvrir la page du track"
                >
                    <ExternalLink className="w-4 h-4" />
                </Link>

                <button
                    onClick={stop}
                    className="p-1.5 md:p-2 text-[#A8A8A8] hover:text-[#F4F4F4] transition-colors shrink-0"
                    data-testid="player-close"
                    aria-label="Fermer le lecteur"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
}
